import { gatewayService } from '@/lib/gateway-service';
import { envConfig } from '@/lib/env-config';
import { supabase } from '@/lib/supabase';
import {
  fetchAndProcessTranscriptWithTiming,
  type ProcessedTranscript,
} from '@/lib/transcript-utils';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ScribePart {
  id: string;
  scribe_job_id: string;
  part_number: number;
  status: string;
  text_file_key: string;
  json_file_key?: string;
  audio_file_key?: string;
  start_time?: string | null;
  end_time?: string | null;
  is_final_part?: boolean | null;
}

interface UseLiveTranscriptRealtimeConfig {
  scribeJobId?: string;
  onTranscriptUpdate: (newTranscriptPart: ProcessedTranscript) => void;
  enabled?: boolean;
}

export function useLiveTranscriptRealtime({
  scribeJobId,
  onTranscriptUpdate,
  enabled = true,
}: UseLiveTranscriptRealtimeConfig) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const subscriptionRef = useRef<any>(null);
  const processedKeysRef = useRef<Set<string>>(new Set());

  // Keep a stable ref to the callback so the channel subscription doesn't churn
  const onTranscriptUpdateRef = useRef(onTranscriptUpdate);
  onTranscriptUpdateRef.current = onTranscriptUpdate;

  // Fetch and process transcript data (JSON or text)
  const processCompletedPart = useCallback(
    async (fileKey: string) => {
      console.log('🎯 processCompletedPart CALLED with:', { fileKey });

      // Avoid duplicate processing
      if (processedKeysRef.current.has(fileKey)) {
        console.log('⚠️ DUPLICATE KEY - Already processed:', fileKey);
        return;
      }

      console.log('➕ Adding key to processed set:', fileKey);
      processedKeysRef.current.add(fileKey);

      try {
        console.log('🚀 STARTING processing for file_key:', fileKey);

        // Get signed URL from gateway (files are in GCS for global recording)
        console.log('☁️ Calling gateway getSignedUrl...');
        const response = await gatewayService.getSignedUrl(fileKey);

        console.log('📥 Gateway response:', {
          success: response.success,
          hasData: !!response.data,
        });

        if (!response.success || !response.data) {
          throw new Error(
            `Failed to get signed URL: ${response.error || 'No data'}`
          );
        }

        const data = response.data as { signedUrl?: string };
        if (!data.signedUrl) {
          throw new Error('No signed URL in response');
        }

        console.log('🌐 Fetching transcript from signed URL:', data.signedUrl);

        const processedPart = await fetchAndProcessTranscriptWithTiming(
          data.signedUrl
        );

        if (processedPart && processedPart.clickableLines.length > 0) {
          const modifiedProcessedPart = {
            ...processedPart,
            clickableLines: processedPart.clickableLines.map((line) => {
              if (!line.text.includes(line.participant)) {
                return {
                  ...line,
                  text: `${line.participant}: ${line.text}`,
                };
              }
              return line;
            }),
          };
          onTranscriptUpdateRef.current(modifiedProcessedPart);
        } else {
          console.warn(
            '⚠️ Processed part was empty or invalid, skipping update.'
          );
        }

        console.log('✅ PROCESSING COMPLETE for:', fileKey);
      } catch (err) {
        console.error('💥 ERROR processing completed part:', {
          fileKey,
          error: err,
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          errorStack: err instanceof Error ? err.stack : undefined,
        });

        // Remove from processed set to allow retry
        console.log('🔄 Removing from processed set for retry:', fileKey);
        processedKeysRef.current.delete(fileKey);
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    },
    []
  );

  // Subscribe to realtime updates
  useEffect(() => {
    if (!enabled || !scribeJobId) {
      console.log('🚫 Subscription disabled:', { enabled, scribeJobId });
      return;
    }

    console.log(
      '🔌 Setting up realtime subscription for scribeJobId:',
      scribeJobId
    );
    console.log('🎛️ Subscription config:', {
      enabled,
      scribeJobId,
      schema: envConfig.supabase.schema || 'eyevantage',
      table: 'ev_scribe_parts',
      event: 'UPDATE',
      filter: `scribe_job_id=eq.${scribeJobId}`,
    });

    const channelName = `ev_scribe_parts_${scribeJobId}`;
    console.log('📺 Creating channel with name:', channelName);
    const channel = supabase.channel(channelName);
    console.log('📺 Channel created:', channel);

    // Subscribe to UPDATE events only, filtered by scribe_job_id
    const subscription = channel
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: envConfig.supabase.schema || 'eyevantage',
          table: 'ev_scribe_parts',
          filter: `scribe_job_id=eq.${scribeJobId}`,
        },
        (payload: RealtimePostgresChangesPayload<ScribePart>) => {
          // 🔍 COMPREHENSIVE LOGGING - Raw update object
          console.log('🚨 COMPLETE REALTIME UPDATE RECEIVED:');
          console.log(
            '📋 Full payload object:',
            JSON.stringify(payload, null, 2)
          );
          console.log('📊 Payload structure breakdown:', {
            eventType: payload.eventType,
            schema: payload.schema,
            table: payload.table,
            commit_timestamp: payload.commit_timestamp,
            errors: payload.errors,
            hasNew: !!payload.new,
            hasOld: !!payload.old,
            newData: payload.new,
            oldData: payload.old,
          });

          const updatedPart = payload.new as ScribePart;
          console.log('🔍 Extracted updatedPart:', updatedPart);

          if (!updatedPart) {
            console.log('❌ No updatedPart found in payload.new');
            return;
          }

          // Log all properties of the updated part
          console.log('📝 Updated part details:', {
            id: updatedPart.id,
            scribe_job_id: updatedPart.scribe_job_id,
            part_number: updatedPart.part_number,
            status: updatedPart.status,
            text_file_key: updatedPart.text_file_key,
            json_file_key: updatedPart.json_file_key,
            audio_file_key: updatedPart.audio_file_key,
            start_time: updatedPart.start_time,
            end_time: updatedPart.end_time,
            is_final_part: updatedPart.is_final_part,
          });

          // Check each condition separately for debugging
          const statusCheck = updatedPart.status === 'Completed';
          const hasJsonFileKey = !!updatedPart.json_file_key;
          const hasTextFileKey = !!updatedPart.text_file_key;

          console.log('🧪 Condition checks:', {
            'status === "Completed"': statusCheck,
            'actual status': updatedPart.status,
            'has json_file_key': hasJsonFileKey,
            'json_file_key value': updatedPart.json_file_key,
            'has text_file_key': hasTextFileKey,
            'text_file_key value': updatedPart.text_file_key,
            'has any file key': hasJsonFileKey || hasTextFileKey,
          });

          // Process completed parts with either json_file_key (preferred) or text_file_key (fallback)
          if (statusCheck && (hasJsonFileKey || hasTextFileKey)) {
            if (hasJsonFileKey) {
              console.log(
                '✅ CONDITIONS MET - Processing JSON completed part:',
                {
                  part_number: updatedPart.part_number,
                  json_file_key: updatedPart.json_file_key,
                  status: updatedPart.status,
                }
              );
              processCompletedPart(updatedPart.json_file_key); // Process as JSON
            } else {
              console.log(
                '✅ CONDITIONS MET - Processing text completed part (fallback):',
                {
                  part_number: updatedPart.part_number,
                  text_file_key: updatedPart.text_file_key,
                  status: updatedPart.status,
                }
              );
              processCompletedPart(updatedPart.text_file_key); // Process as text
            }
          } else {
            console.log('❌ CONDITIONS NOT MET - Skipping part:', {
              reason: !statusCheck
                ? 'status is not "Completed"'
                : 'no file keys available',
              status: updatedPart.status,
              json_file_key: updatedPart.json_file_key,
              text_file_key: updatedPart.text_file_key,
            });
          }
        }
      )
      .subscribe((status, err) => {
        console.log(`📡 Subscription status changed: ${status}`);
        console.log('📡 Status details:', {
          status,
          err,
          channelName,
          scribeJobId,
        });

        if (status === 'SUBSCRIBED') {
          console.log('✅ SUCCESSFULLY SUBSCRIBED to realtime updates!');
          setIsConnected(true);
          setError(null);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ CHANNEL ERROR:', err);
          setIsConnected(false);
          setError(err?.message || 'Subscription error');
        } else {
          console.log(`ℹ️ Other status: ${status}`, err);
        }
      });

    subscriptionRef.current = channel;

    console.log('🎉 Realtime subscription setup complete!', {
      channelName,
      scribeJobId,
      subscriptionRef: !!subscriptionRef.current,
    });

    // Cleanup function
    return () => {
      console.log('🧹 Cleaning up realtime subscription for:', scribeJobId);
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
      setIsConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scribeJobId]);

  // Reset processed keys when scribeJobId changes
  useEffect(() => {
    processedKeysRef.current.clear();
    setError(null);
  }, [scribeJobId]);

  return {
    isConnected,
    error,
  };
}
